import {GuiButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/GuiButton";
import {SpinePlayer} from "../../core/utils/SpinePlayer";
import {Layers, RockBottomLayers, SimpleStageLayer} from "./RockBottomLayers";
import {RockBottom} from "../RockBottom";
import {RockBottomGameConfig} from "../RockBottomGameConfig";
import {BonusFeatureData} from "@nolimitcity/slot-launcher/bin/plugins/apiplugin/BonusFeatures";
import {gsap, TweenLite} from "gsap";
import {RockBottomSound} from "../sound/RockBottomSound";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";

export class RockBottomXBetView extends PIXI.Container {
    private _layer: SimpleStageLayer | undefined;
    private _xBetButton: RockBottomXBetButton;
    private _xBetAllowed: boolean = false;
    private _xBetPaused: boolean = false;
    private _xBetReelFrameSpine: PIXI.spine.Spine;
    private _xBetMainTL: gsap.core.Timeline | null;
    private _enableTl: TimelineLite;

    constructor() {
        super();
        this.init();
        RockBottom.apiPlugin.events.on(APIEvent.SELECTED_FEATURE_BET_CHANGED, () => this.onBetFeature());
    }

    private onBetFeature(): void {
        const active = RockBottom.apiPlugin.betFeatureController.getActiveBetFeature();
        if (active?.name == "BOOSTED_BET") {
            RockBottom.xBetView.turnOnXBet(false);
        } else  {
            RockBottom.xBetView.turnOffXBet(false);
        }
    }

    public enableXBetBtn(value: boolean, delay: number): void {
        if (this._xBetButton) {
            if (!this._xBetAllowed || this._xBetPaused) {
                this._xBetButton.enable(false);
                return;
            }
            // #305
            if (this._enableTl) {
                this._enableTl.pause();
                this._enableTl.kill();
            }

            if (RockBottom.apiPlugin.isReplay || RockBottom.apiPlugin.freeBets.hasFreeBets() || RockBottom.apiPlugin.freeFeatureBet.hasFreeFeatureBet()) {
                value = false
            }

            const tl: gsap.core.Timeline = gsap.timeline();
            tl.add(() => {
                this._xBetButton.enable(value);
                this._xBetButton.playXBetIdleAnim();
            }, delay);

            this._enableTl = tl;

        }
    }

    public getXBetButton(): RockBottomXBetButton {
        return this._xBetButton;
    }

    public toggleXBetFlushAnim(show: boolean, playOnlyIdle: boolean = false): void {
        if (RockBottomGameConfig.IS_BOOSTED_BET) {
            if (show) {
                this.playXBetFlushAnim(playOnlyIdle);
            } else {
                this.resetXBetReelFrame();
            }
        }
    }

    public turnOnXBet(pauseBoost: boolean) {
        if (!this._xBetAllowed) {
            return;
        }
        if (!this._xBetButton.toggled) {
            this.toggleXBetButton(false);
        }
        this._xBetPaused = pauseBoost;
    }

    public turnOffXBet(pauseBoost: boolean) {
        if (!this._xBetAllowed) {
            return;
        }
        if (this._xBetButton.toggled) {
            this.toggleXBetButton(false);
        }
        this._xBetPaused = pauseBoost;
    }

    private init(): void {
        this._xBetAllowed = RockBottom.isBoostedBetAllowed();
        if (this._xBetAllowed) {
            this._layer = RockBottomLayers.getLayer(Layers.X_BET);
            this._xBetMainTL = gsap.timeline();
            this.createRightFlush();
            this.createXBetButton();
            this.createXBetReelFrame();
            this._layer?.addChild(this);
        }
    }

    private createRightFlush(): void {
        const sprite: PIXI.Sprite = new PIXI.Sprite(RockBottom.assetLoader.getTextures("rightFlush")[0]);
        sprite.anchor.set(0.5, 0.5);
        sprite.position.set(609, 94);
        sprite.scale.set(-0.9, 0.99);
        this.addChild(sprite);
    }

    private createXBetButton(): void {
        this._xBetButton = new RockBottomXBetButton();
        this._xBetButton.addClickCallback(() => {
            //this.toggleXBetButton(true);
            const active = RockBottom.apiPlugin.betFeatureController.getActiveBetFeature();
            if (active?.name == "BOOSTED_BET"){
                RockBottom.apiPlugin.betFeatureController.setActiveBetFeature();
                RockBottomSound.soundPlayer.stopTrack(RockBottomSound.BOOSTED_BET_ON);
                RockBottomSound.soundPlayer.playTrack({
                    name: RockBottomSound.BOOSTED_BET_OFF
                });
            } else {
                RockBottom.apiPlugin.betFeatureController.setActiveBetFeature("BOOSTED_BET");
                RockBottomSound.soundPlayer.playTrack({
                    name: RockBottomSound.BOOSTED_BET_ON
                });
                RockBottomSound.soundPlayer.stopTrack(RockBottomSound.BOOSTED_BET_OFF);
            }
        });
        this._xBetButton.toggled = false;
        this._xBetButton.enable(false);
        this.addChild(this._xBetButton);
    }

    private createXBetReelFrame(): void {
        this._xBetReelFrameSpine = new PIXI.spine.Spine(RockBottom.assetLoader.getSpineAsset("xBet"));
        this.resetXBetReelFrame();
        this._xBetReelFrameSpine.position.set(215, 360);
        this.addChild(this._xBetReelFrameSpine);
    }

    private resetXBetReelFrame(): void {
        if (this._xBetMainTL) {
            this.resetXBetMainTl();
        }
        this._xBetMainTL = gsap.timeline();
        this._xBetMainTL.to(this._xBetReelFrameSpine, {
            duration: 0.3,
            alpha: 0,
            onComplete: () => {
                this._xBetReelFrameSpine.visible = false;
                this._xBetReelFrameSpine.state.clearTracks();
                this._xBetReelFrameSpine.skeleton.setToSetupPose();
                this._xBetReelFrameSpine.skeleton.setSlotsToSetupPose();
                this._xBetReelFrameSpine.state.setEmptyAnimation(0, 0);
            }
        });
    }

    private playXBetFlushAnim(playOnlyIdle: boolean = false): void {
        if (this._xBetMainTL) {
            this.resetXBetMainTl();
        }

        this._xBetReelFrameSpine.visible = true;
        this._xBetReelFrameSpine.alpha = 1;
        this._xBetReelFrameSpine.updateTransform();

        const flushIdleAnim: string = "Flush_Water_Idle";

        if (playOnlyIdle) {
            this._xBetReelFrameSpine.state.setAnimation(0, flushIdleAnim, true);
        } else {
            const leverDownAnim: string = "On",
                leverDownDuration: number = this._xBetReelFrameSpine.skeleton.data.findAnimation(leverDownAnim).duration,
                flushOnAnim: string = "Flush_Water_On",
                flushOnDuration: number = this._xBetReelFrameSpine.skeleton.data.findAnimation(flushOnAnim).duration,
                tl: gsap.core.Timeline = gsap.timeline();

            tl.to(this, {
                duration: leverDownDuration,
                onComplete: () => {
                    this._xBetReelFrameSpine.state.setAnimation(0, flushOnAnim, false);
                    this._xBetReelFrameSpine.state.addAnimation(0, flushIdleAnim, true, flushOnDuration);
                }
            });
        }
    }

    private toggleXBetButton(isFromClick: boolean): void {
        if (!this._xBetAllowed || this._xBetPaused) {
            return;
        }

        if (this._xBetButton.toggled) {
            RockBottomGameConfig.IS_BOOSTED_BET = false;
            this._xBetButton.turnOnOffXBet("Off");
            this.resetXBetReelFrame();
        } else {
            const featureData: BonusFeatureData | undefined = RockBottom.apiPlugin.bonusFeatures.getFeatureDataByName("BOOSTED_BET");
            if (featureData) {
                RockBottomGameConfig.IS_BOOSTED_BET = true;
                this._xBetButton.turnOnOffXBet("On");
                this.playXBetFlushAnim();
            }
        }

        this._xBetButton.toggled = !this._xBetButton.toggled;
    }

    private resetXBetMainTl(): void {
        if (this._xBetMainTL && this._xBetMainTL.isActive()) {
            this._xBetMainTL.paused();
            this._xBetMainTL.kill();
            this._xBetMainTL = null;
        }
    }
}

export class RockBottomXBetButton extends GuiButton {
    public toggled: boolean = false;
    private _btnSpine: SpinePlayer;

    constructor() {
        super("xBetButton");
        this.init();
    }

    public turnOnOffXBet(animName: string): void {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(this._btnSpine.play({
            name: animName,
            onComplete: () => {
                if (animName === "Off") {
                    TweenLite.to(this, {
                        delay: 0.3,
                        onStart: () => {
                            this.playXBetIdleAnim();
                        }
                    });
                }
            }
        }));
    }

    public switchOnBonusGame(isBonusGameEnding: boolean): void {
        if (isBonusGameEnding) {
            if (RockBottomGameConfig.IS_BOOSTED_BET) {
                this._btnSpine.play({name: "On"});
            }
        } else {
            if (RockBottomGameConfig.IS_BOOSTED_BET) {
                this._btnSpine.play({name: "On_FS"});
            }
        }
    }

    public stopXBetIdleAnim(): void {
        if (!(RockBottomGameConfig.IS_BOOSTED_BET)) {
            this._btnSpine.play({name: "Static"});
        }
    }

    public playXBetIdleAnim(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (RockBottom.apiPlugin.isReplay || RockBottomGameConfig.IS_BOOSTED_BET || RockBottom.apiPlugin.freeBets.hasFreeBets() || RockBottom.apiPlugin.freeFeatureBet.hasFreeFeatureBet()) {
            return tl;
        }

        tl.add(this._btnSpine.play({
            name: "Off_idle",
            loop: true
        }));

        return tl;
    }

    private init(): void {
        this.createButton();
    }

    private createButton(): void {
        this._btnSpine = new SpinePlayer("xBet");
        this.stopXBetIdleAnim();
        this.position.set(108, 94);
        this.addChild(this._btnSpine);
    }
}
